import {
  BaseFullSchema_OLD,
  BaseListViewSchema,
  BaseRefSchema,
  BaseCreateSchema,
  BaseUpdateSchema,
  BaseUpdateResponseSchema,
  PaginatedResponse,
  IdAsUUID,
} from "./common/commonTypes";
import {
  PrimaryOrderStatusType,
  ApprovalStatusType,
} from "./common/statusTypes";
import { PaymentTermType } from "./common/termTypes";
import { VendorRef } from "./companyTypes";
import { ItemRef } from "./itemTypes";
import { UOMUnitRef } from "./uomTypes";
import { InvMovRef } from "./invMovementTypes";
import { EmployeeRef } from "./personTypes";
import { LocationRef } from "./locationTypes";
import { InvLotReceipt } from "./lotTypes";
import { TableObject } from "./common/baseTableTypes";

export const PURCH_ORD_REC_STATUSES = [
  "APPROVAL",
  "REJECTED",
  "RECEIPT",
  "PARTIAL",
  "FULL",
  "COMPLETE",
  "VOID",
] as const;
export type PurchaseOrderReceiptStatusType =
  (typeof PURCH_ORD_REC_STATUSES)[number];

export interface PurchOrderFull extends BaseFullSchema_OLD {
  number: string;
  order_terms: PaymentTermType;
  order_date: string;
  expected_date: string | null;
  order_status: PrimaryOrderStatusType;
  approval_status: ApprovalStatusType;
  receipt_status: PurchaseOrderReceiptStatusType;
  tax_rate: string;
  tax_amount: string;
  ship_cost: string;
  subtotal: string;
  notes: string | null;
  is_blanket: boolean;
  is_dropship: boolean;
  billing_addr: TableObject<"orderaddress">;
  shipping_addr: TableObject<"orderaddress">;

  vendor: VendorRef;
  employee: EmployeeRef;
  line_items: POLineFull[];
  location: LocationRef;
}

export interface PurchOrderListView extends BaseListViewSchema {
  number: string;
  order_terms: PaymentTermType;
  order_date: string;
  expected_date: string | null;
  order_status: PrimaryOrderStatusType;
  approval_status: ApprovalStatusType;
  receipt_status: PurchaseOrderReceiptStatusType;
  subtotal: string;
  items_ordered: string[];
}

export interface PurchOrderRef extends BaseRefSchema {
  number: string;
  order_status: PrimaryOrderStatusType;
  approval_status: ApprovalStatusType;
  receipt_status: PurchaseOrderReceiptStatusType;
}

export interface PurchOrderCreate extends BaseCreateSchema {
  number: string | null;
  vendor_id: IdAsUUID;
  employee_id: IdAsUUID;
  location_id: IdAsUUID;
  order_terms: PaymentTermType;
  order_date: string;
  expected_date: string | null;
  tax_rate: number;
  tax_amount: number;
  ship_cost: number;
  notes: string | null;
  is_blanket: boolean;
  is_dropship: boolean;
  billing_addr: TableObject<"orderaddress">;
  shipping_addr: TableObject<"orderaddress">;
  line_items: POLineCreate[];
}

export interface PurchOrderUpdate extends BaseUpdateSchema {
  number?: string;
  vendor_id?: IdAsUUID;
  employee_id?: IdAsUUID;
  location_id?: IdAsUUID;
  order_terms?: PaymentTermType;
  order_date?: string;
  expected_date?: string | null;
  order_status?: PrimaryOrderStatusType;
  approval_status?: ApprovalStatusType;
  receipt_status?: PurchaseOrderReceiptStatusType;
  tax_rate?: number;
  tax_amount?: number;
  ship_cost?: number;
  notes?: string;
  is_blanket?: boolean;
  is_dropship?: boolean;
  billing_addr?: TableObject<"orderaddress">;
  shipping_addr?: TableObject<"orderaddress">;

  line_items_to_update?: POLineUpdate[];
  line_items_to_add?: POLineCreate[];
  line_item_ids_to_delete?: IdAsUUID[];
}

export interface PurchOrderStatusUpdate extends BaseUpdateSchema {
  new_status: ApprovalStatusType;
}

export interface POLineFull extends BaseFullSchema_OLD {
  line: number;
  item_name_override: string | null;
  expected_date: string | null;
  revision_ordered: string;
  quantity_ordered: string;
  quantity_ordered_base: string;
  quantity_received: string;
  quantity_received_base: string;
  unit_cost: string;
  line_total: string;
  notes: string | null;
  requires_receipt: boolean;
  line_completed: boolean;
  is_taxed: boolean;

  item: ItemRef;
  receipts: InvMovRef[];
  uom_unit: UOMUnitRef;
}

export interface POLineRef extends BaseRefSchema {
  line: number;
  item_name_override?: string;
  revision_ordered: string;
  quantity_ordered: string;
  quantity_received: string;
  unit_cost: string;
  line_total: string;
  notes?: string;
  requires_receipt: boolean;
  line_completed: boolean;

  item: ItemRef;
  uom_unit: UOMUnitRef;
}

export interface POLineCreate extends BaseCreateSchema {
  item_id: IdAsUUID;
  uom_unit_id: IdAsUUID;
  quantity_ordered: number;
  unit_cost: number;
  notes: string | null;
  revision_ordered: string;
  requires_receipt: boolean;
  item_name_override: string | null;
  expected_date: string | null;
  is_taxed: boolean;
  work_order_data: null;
}

export interface POLineUpdate {
  id: IdAsUUID;
  item_id?: IdAsUUID;
  uom_unit_id?: IdAsUUID;
  quantity_ordered?: number;
  unit_cost?: number;
  notes?: string | null;
  revision_ordered?: string;
  requires_receipt?: boolean;
  item_name_override?: string | null;
  expected_date?: string | null;
  is_taxed?: boolean;
  work_order_data?: null;
}

export interface POLineReceipt {
  line_id: IdAsUUID;
  lot_data: InvLotReceipt[];
  mark_line_complete: boolean;
}

export interface POReceiving {
  rec_lines: POLineReceipt[];
}

export interface POReceiptResponse extends BaseUpdateResponseSchema {
  id: IdAsUUID;
  number: string;
  vendor: string;
  lots: ReceivedLot[];
}

export interface ReceivedLot {
  id: IdAsUUID;
  number: string;
  item: string;
  recrev: string;
  recquant: string;
  ordquant: string;
  ordunit: string;
  po_line_id: IdAsUUID;
}

export interface ReceivingDocuments extends BaseUpdateResponseSchema {
  doc_links: { [key: string]: string };
}

export type PaginatedPOResponse = PaginatedResponse<PurchOrderFull>;
